import { CategoryCalcNode } from "../editor/AlternativesEditor";
import { makeDeduper } from "../util/dedup";
import deepCopy from "../util/deepCopy";
import { Category, CategoryNode, EuropeOrAllyCountry, ProductOrBrand, SubCategory } from "./alternativeTypes";
import { TFunction } from "i18next";


export const productsAreSame = (a: ProductOrBrand, b: ProductOrBrand) => {
  if (a.id || b.id) {
    return a.id === b.id;
  }
  return a.name === b.name && a.categories.length === b.categories.length && a.ownerCountry === b.ownerCountry;
};

const isAvailableIn = (country: EuropeOrAllyCountry, brand: ProductOrBrand) => {
  if (country == "EU") {
    if (!brand.availableIn && !brand.notAvailableIn) {
      return true;
    }
    const points = (isAvailableIn("DE", brand) ? 2 : 0)
      + (isAvailableIn("FR", brand) ? 2 : 0)
      + (isAvailableIn("ES", brand) ? 1 : 0)
      + (isAvailableIn("PL", brand) ? 1 : 0)
      + (isAvailableIn("NL", brand) ? 1 : 0)
      + (isAvailableIn("RO", brand) ? 1 : 0)
      + (isAvailableIn("SE", brand) ? 1 : 0);
    return points >= 3;
  }
  if (brand.availableIn) {
    return brand.availableIn.includes(country)
  }
  if (brand.notAvailableIn) {
    return !brand.notAvailableIn.includes(country)
  }
  return true;
}

const isMainCategoryWithSubcategories = (categoryId: Category, categoryTree: CategoryNode[]) => {
  const node = categoryTree.find((n) => n.category === categoryId);
  if (node) {
    return node.subcategories && node.subcategories.length > 0;
  }
  return false;
}

const calcNodeFromCategoryNode = (c: CategoryNode): CategoryCalcNode => {
  return {
    id: c.category,
    name: c.category,
    avoid: [],
    consider: [],
    include: false,
    subcategories: c.subcategories
      ? c.subcategories.map((sc) => calcNodeFromCategoryNode(sc))
      : []
  }
}

const findNode = (categoryId: string, cats: CategoryCalcNode[]): CategoryCalcNode => {
  if (!cats) {
    return undefined;
  }
  for (let cat of cats) {
    const found = findNode(categoryId, cat.subcategories);
    if (found) {
      return found;
    }
    if (categoryId === cat.id) {
      return cat;
    }
  }
  return undefined;
}

export const makeCategories = (
  products: ProductOrBrand[],
  country: EuropeOrAllyCountry,
  include: Category[],
  categoryTree: CategoryNode[],
  shouldAvoid: (p: ProductOrBrand) => boolean,
  shouldConsider: (p: ProductOrBrand) => boolean,
  shouldIncludeByDefault: (p: ProductOrBrand) => boolean,
  dt: TFunction<any, undefined>,
): CategoryCalcNode[] => {

  const cats: CategoryCalcNode[] = categoryTree
    .map((c) => calcNodeFromCategoryNode(c));
  for (let cat of cats) {
    cat.name = dt("default." + cat.id)
    cat.include = include.includes(cat.id as Category);
  }
  cats.sort((a, b) => (a.include ? include.indexOf(a.id as Category) : 1000) - (b.include ? include.indexOf(b.id as Category) : 1000))

  // Put all brands into leaf nodes.
  // Categories that don't have subcategories are also added.
  for (let brand of products) {
    for (let i = 0; i < brand.categories.length; i++) {
      const categoryId = brand.categories[i];
      if (isMainCategoryWithSubcategories(categoryId, categoryTree)) {
        if (brand.categories.length === 1) {
          console.log("warning: Missing subcategory for brand " + brand.name)
        }
        continue;
      }
      const cat = findNode(categoryId, cats);
      if (!cat) {
        console.log("warning: Cannot find category with id " + categoryId)
        continue;
      }
      if (shouldAvoid(brand)) {
        const brandCopy = deepCopy(brand);
        if (brandCopy.include !== false) {
          brandCopy.include = isAvailableIn(country, brand);
        }          
        cat.avoid.push(brandCopy)
      } else if (shouldConsider(brand)) {
        const brandCopy = deepCopy(brand);
        if (!cat.consider.find((p) => p.include && p.name === brandCopy.name)
          && shouldIncludeByDefault(brandCopy))
        {
          brandCopy.include = isAvailableIn(country, brand);
        }
        cat.consider.push(brandCopy)
      }
    }
  }

  // Uninclude subcategories that don't have included items on both sides
  for (let cat of cats) {
    if (cat.subcategories) {
      const avoidSubcatSet = new Set<Category>();
      const considerSubcatSet = new Set<Category>();
      for (let subcat of cat.subcategories) {
        if (subcat.avoid.filter((p) => p.include).length > 0) {
          avoidSubcatSet.add(subcat.id as SubCategory);
        }
        if (subcat.consider.filter((p) => p.include).length > 0) {
          considerSubcatSet.add(subcat.id as SubCategory);
        }
      }
      const doesProductShareSubcatsWith = (p: ProductOrBrand, subcatSet: Set<Category>) => {
        return !!p.categories.find((sc) => subcatSet.has(sc));
      }
      for (let subcat of cat.subcategories) {
        const hasIncludedAvoidProduct = !!subcat.avoid.find((p) => p.include);
        const hasIncludedConsiderProduct = !! subcat.consider.find((p) => p.include);
        if (!hasIncludedAvoidProduct || !hasIncludedConsiderProduct) {
          console.log("Uninclude cause not on both sides:", subcat.id);
          subcat.avoid.forEach((p) => { p.include = p.include && doesProductShareSubcatsWith(p, considerSubcatSet); });
          subcat.consider.forEach((p) => { p.include = p.include && doesProductShareSubcatsWith(p, avoidSubcatSet); });
        }
      }
    }
  }

  // Sort entries relating to country first
  const relatesToCountry = (p: ProductOrBrand) => {
    return p.ownerCountry === country
      || p.headquarterIn === country
  }
  const sortRelatedFirst = (cat: CategoryCalcNode) => {
    cat.consider = [
      ...cat.consider.filter((p) => relatesToCountry(p)),
      ...cat.consider.filter((p) => !relatesToCountry(p))
    ]
    if (cat.subcategories) {
      for (let subcat of cat.subcategories) {
        sortRelatedFirst(subcat);
      }
    }
  }
  for (let cat of cats) {
    sortRelatedFirst(cat);
  }

  // Copy products into main categories
  for (let cat of cats) {
    copyProductsIntoMainCategory(cat);
  }

  // Make sure defaults aren't too long.
  // Uninclude items when char count is over limit
  const limit = 110;
  for (let cat of cats) {
    while (true) {
      const [ avoid, consider ] = avoidAndConsiderStringReps(cat);
      if (avoid.length <= limit && consider.length <= limit) {
        break;
      }
      if (avoid.length > limit) {
        console.log("over limit avoid:", avoid);
        let bestScore = 0;
        let bestSubcat: CategoryCalcNode = undefined;
        let bestUninclude: ProductOrBrand = undefined;
        const candidateCats = cat.subcategories.filter((sc) => sc.avoid.filter((p) => p.include).length > 1);
        for (let cc of candidateCats) {
          for (let p of cc.avoid.filter((p) => p.include)) {
            // Uninclude product and calc score
            const testCat = deepCopy(cat);
            const testSubcat = testCat.subcategories.find((sc) => sc.id === cc.id);
            const testP = testSubcat.avoid.find((tp) => productsAreSame(tp, p));
            testP.include = false;
            copyProductsIntoMainCategory(testCat);
            const score = includeScore(testCat, country)
            if (score >= bestScore) {
              bestScore = score;
              bestSubcat = testSubcat;
              bestUninclude = testP;
            }
          }
        }
        if (!bestSubcat) {
          break;
        }
        console.log("bestSubcat:", avoidAndConsiderStringReps(bestSubcat)[1], "without", bestUninclude.name);
        // replace bestSubcat
        cat.subcategories = cat.subcategories
          .map((sc) => (bestSubcat.id === sc.id ? bestSubcat : sc))
        copyProductsIntoMainCategory(cat);
      }
      if (consider.length > limit) {
        console.log("over limit consider:", consider);
        let bestScore = 0;
        let bestSubcat: CategoryCalcNode = undefined;
        let bestUninclude: ProductOrBrand = undefined;
        const candidateCats = cat.subcategories.filter((sc) => sc.consider.filter((p) => p.include).length > 1);
        for (let cc of candidateCats) {
          for (let p of cc.consider.filter((p) => p.include)) {
            // Uninclude product and calc score
            const testCat = deepCopy(cat);
            const testSubcat = testCat.subcategories.find((sc) => sc.id === cc.id);
            const testP = testSubcat.consider.find((tp) => productsAreSame(tp, p));
            testP.include = false;
            copyProductsIntoMainCategory(testCat);
            const score = includeScore(testCat, country)
            if (score >= bestScore) {
              bestScore = score;
              bestSubcat = testSubcat;
              bestUninclude = testP;
            }
          }
        }
        if (!bestSubcat) {
          break;
        }
        console.log("bestSubcat:", avoidAndConsiderStringReps(bestSubcat)[1], "without", bestUninclude.name);
        // replace bestSubcat
        cat.subcategories = cat.subcategories
          .map((sc) => (bestSubcat.id === sc.id ? bestSubcat : sc))
        copyProductsIntoMainCategory(cat);
      }
    }
  }

  return cats;

};

const copyProductsIntoMainCategory = (cat: CategoryCalcNode) => {
  const deduper = makeDeduper(productsAreSame);
  cat.avoid = cat.subcategories && cat.subcategories.length > 0
  ? cat.subcategories
    .flatMap((sc) => sc.avoid)
    .reduce(deduper, [])
  : cat.avoid;
cat.consider = cat.subcategories && cat.subcategories.length > 0
  ? cat.subcategories
    .flatMap((sc) => sc.consider)
    .reduce(deduper, [])
  : cat.consider;
}

const avoidAndConsiderStringReps = (cat: CategoryCalcNode) => {
  const avoidIncluded = cat.avoid.filter((p) => p.include);
  const avoid = avoidIncluded.map((p) => p.name).join(", ");
  const considerIncluded = cat.consider.filter((p) => p.include);
  const consider = considerIncluded.map((p) => p.name).join(", ");
  return [ avoid, consider ];
}

const includeScore = (cat: CategoryCalcNode, country: EuropeOrAllyCountry): number => {
  let altCountScore = 0;
  let sameCountryScore = 0
  const countrySet = new Set();
  for (let subcat of cat.subcategories) {
    const avoidIncluded = subcat.avoid.filter((p) => p.include);
    const considerIncluded = subcat.consider.filter((p) => p.include);
    if (avoidIncluded.length > 0 && considerIncluded.length > 0) {
      altCountScore += 10000;
    }
    if (avoidIncluded.length > 1) {
      altCountScore += 16;
    }
    if (considerIncluded.length > 1) {
      altCountScore += 16;
    }    
    if (avoidIncluded.length > 2) {
      altCountScore += 8;
    }
    if (considerIncluded.length > 2) {
      altCountScore += 8;
    }    
    for (let p of considerIncluded) {
      const pCountry = p.headquarterIn || p.ownerCountry;
      if (pCountry === country) {
        sameCountryScore += 10;
      }
      countrySet.add(p.headquarterIn || p.ownerCountry);
    }
  }
  let countrySetScore = 100 * countrySet.size;
  const [avoid, consider] = avoidAndConsiderStringReps(cat);
  let textLengthScore = 220 - avoid.length - consider.length;
  /* console.log(
    "altCountScore:", altCountScore,
    "sameCountryScore:", sameCountryScore,
    "countrySetScore:", countrySetScore,
    "textLengthScore:", textLengthScore) 
  */
  return altCountScore + sameCountryScore + countrySetScore + textLengthScore;
}

